import React from 'react';
import { Box, Container, Typography, Card, CardContent, Grid, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import ConstructionIcon from '@mui/icons-material/Construction';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GroupWorkIcon from '@mui/icons-material/GroupWork';

const ProjectHighlights = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const projects = [
    {
      icon: <ConstructionIcon sx={{ fontSize: 40 }} />,
      title: 'Branding & Market Development',
      description: 'Conceptualized and executed branding and market development for wires products, achieving 500 tons of sales and launching new brands (Nails/Binding wires)',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: 'Sales Growth - Iron Rods',
      description: 'Spearheaded sales growth of Iron Rods, driving a 14% increase in volumes (6,000 tons to 7,000 tons) and a 15% boost in margins within 4 months',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
      title: 'Product Profitability Enhancement',
      description: 'Enhanced product profitability through strategic pricing and market positioning, increasing overall margins and volumes',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <GroupWorkIcon sx={{ fontSize: 40 }} />,
      title: 'Sales Strategy & Team Restructuring',
      description: 'Developed and implemented sales strategies, restructuring the sales team by territories to maximize coverage across Ghana and neighboring land border countries',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: 'linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%)',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          sx={{
            textAlign: 'center',
            mb: { xs: 1.5, md: 2 },
            fontWeight: 700,
            background: 'linear-gradient(90deg, #0968E5, #091970)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
          }}
        >
          Project Highlights
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 5, md: 6 },
            fontWeight: 400,
            color: '#666',
            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
          }}
        >
          Steel Company, Ghana
        </Typography>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Grid container spacing={3}>
            {projects.map((project, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      minHeight: { xs: '280px', sm: '300px' },
                      borderRadius: 4,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid rgba(9, 104, 229, 0.1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 15px 40px rgba(9, 104, 229, 0.2)',
                        border: '1px solid rgba(9, 104, 229, 0.3)',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        height: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: { xs: 60, sm: 65, md: 70 },
                            height: { xs: 60, sm: 65, md: 70 },
                            borderRadius: '50%',
                            background: project.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            mb: { xs: 1.5, md: 2 },
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            '& svg': {
                              display: 'block',
                              margin: 'auto',
                              fontSize: { xs: 32, sm: 36, md: 40 },
                            },
                          }}
                        >
                          {project.icon}
                        </Box>
                        <Typography
                          variant={isMobile ? 'h6' : 'h6'}
                          sx={{
                            fontWeight: 700,
                            mb: { xs: 1.5, md: 2 },
                            color: '#333',
                            fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1.05rem' },
                          }}
                        >
                          {project.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant={isMobile ? 'body2' : 'body2'}
                        sx={{
                          color: '#666',
                          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem', lg: '0.9rem' },
                          lineHeight: { xs: 1.5, md: 1.6 },
                        }}
                      >
                        {project.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ProjectHighlights;




